import { Typography } from '@mui/material';
import styles from './StickyNavBar.module.scss';
import { ContainedButton, LinkButton } from '../../../../../commons/hoc';
import { getProducts } from '../../../../../commons/utils/productsHelper';
import { categoryBasedContent } from './contants';
import { useEffect, useMemo, useState } from 'react';
import { StickyDownArrow } from '../../../../../src/assets/images/icons';
import { Divider, Link } from '../../../../../commons/components/ui';
import { useRouter } from 'next/router';
import { useAppDispatch, useAppSelector } from '../../../../../src/app/hooks';
import { setSelectedVariantData } from '../../../../../src/features/user/userState';
import { ProductsCategory } from '../../../../../config';

const StickyNavBar = ({
  tags = {},
  ctaClick = () => {},
  productKey = '',
  productType = '',
  ...rest
}) => {
  const products = getProducts();
  const { asPath } = useRouter();
  const dispatch = useAppDispatch();
  const [showDropdown, setShowDropDown] = useState(false);
  const [activeHash, setActiveHash] = useState('');
  const [activeText, setActiveText] = useState('');
  const Router = useRouter();
  // Fetch selectedVariant from Redux, set by ProductGalleryInfo
  const selectedVariantFromRedux = useAppSelector(
    (state) => state.user.selectedVariantData
  );
  const [selectedVariant, setSelectedVariant] = useState('DK2');
  useEffect(() => {
    const variantFromQuery = Router.query.variant;

    if (
      [ProductsCategory.VITAMIN_D_K2, ProductsCategory.VITAMIN_D_K5].includes(
        productType
      )
    ) {
      const normalized =
        (variantFromQuery || selectedVariantFromRedux)?.replace('D_', 'D') ||
        'DK2';

      setSelectedVariant(normalized); // update local state

      if (variantFromQuery && variantFromQuery !== selectedVariantFromRedux) {
        dispatch(setSelectedVariantData(variantFromQuery));
      }
    }
  }, [productType, selectedVariantFromRedux, Router.query.variant]);

  const { currentProduct, anchorTags, productInFocus } = useMemo(() => {
    let currentProduct = {};
    let anchorTags = {};
    let productInFocus = '';

    if (
      [ProductsCategory.VITAMIN_D_K2, ProductsCategory.VITAMIN_D_K5].includes(
        productType
      )
    ) {
      const content = categoryBasedContent[selectedVariant];
      currentProduct = products[content?.productKey];
      anchorTags = content?.tags || {};
      productInFocus = content?.productKey;
    } else {
      const content = categoryBasedContent[productType];
      if (productType && content) {
        currentProduct = products[content?.productKey];
        anchorTags = content?.tags || {};
        productInFocus = content?.productKey;
      } else {
        currentProduct = products[productKey];
        anchorTags = { ...tags };
        productInFocus = productKey;
      }
    }
    return { currentProduct, anchorTags, productInFocus };
  }, [productType, selectedVariant, productKey, tags, products]);

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for sticky header
      const sections = Object.entries(anchorTags).map(([tag, data]) => ({
        id: data.href.replace('#', ''),
        offsetTop:
          document.getElementById(data.href.replace('#', ''))?.offsetTop || 0,
      }));

      const currentSection = sections.reduce((prev, curr) => {
        return scrollPosition >= curr.offsetTop ? curr : prev;
      }, sections[0]);

      if (currentSection && activeHash !== `#${currentSection.id}`) {
        setActiveHash(`#${currentSection.id}`);
        setActiveText(anchorTags[currentSection.id]?.text || '');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [anchorTags, activeHash]);

  useEffect(() => {
    let hash = `${asPath.split('#')[1]}` || '';
    let tag = {};
    if (hash && anchorTags.hasOwnProperty(hash)) {
      tag = anchorTags[hash];
    } else {
      tag = Object.values(anchorTags)[0] || { href: '', text: '' };
    }
    setActiveHash(tag.href);
    setActiveText(tag.text);
  }, [asPath, anchorTags]);

  const handleNavClickInternal = (href, text) => {
    setActiveHash(href);
    setActiveText(text);
    setShowDropDown(false);
  };

  return (
    <div
      className={`${styles.stickyNavExtender} ${false ? '' : styles.isSticky}`}
      id="oldNav"
    >
      <div className={styles.stickyNavContainer}>
        <div className={styles.stickyNavContent}>
          <div className={styles.stickyNavDefaultContainer}>
            <div
              className={styles.highlightedProduct}
              onClick={() => {
                if (window)
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
              }}
            >
              <Typography
                variant="h3"
                component="span"
                color="primary.main"
                noWrap
              >
                {currentProduct?.title || 'Product Title'}
              </Typography>
              <Typography
                variant="p1"
                component="span"
                color="action.gray"
                className={styles.subtitle}
                noWrap
              >
                Starting at $
                {parseInt(currentProduct?.initialPrice)
                  ? currentProduct?.initialPrice
                  : currentProduct?.recurringPrice}
              </Typography>
            </div>
            <div className={styles.anchorsContainer}>
              {Object.entries(anchorTags).map(([tag, data], index) => (
                <LinkButton
                  key={index}
                  scroll={false}
                  {...data}
                  className={`${activeHash === data.href ? styles.activeItem : styles.linkBtn}`}
                  onClickHandler={() =>
                    handleNavClickInternal(data.href, data.text)
                  }
                >
                  {data.text}
                </LinkButton>
              ))}
            </div>
            <div className={styles.addToCartBtnContainer}>
              <ContainedButton
                onClick={() => {
                  Router.push('#buy', undefined, {
                    shallow: true,
                    scroll: false,
                  });
                  ctaClick(productInFocus);
                }}
              >
                Buy Now
              </ContainedButton>
            </div>
          </div>
          <Divider stretchScreen={true} className={styles.stickyBottomBorder} />
          <div
            className={styles.responsiveMenu}
            onClick={() => setShowDropDown((old) => !old)}
          >
            <div className={styles.responsiveContainer}>
              <div className={styles.responsiveTitle}>
                <Typography variant="h5" color="primary.main">
                  {activeText}
                </Typography>
              </div>
              <div
                className={`${styles.responsiveArrow} ${
                  showDropdown ? styles.isActive : ''
                }`}
              >
                <StickyDownArrow />
              </div>
            </div>
          </div>

          <Divider stretchScreen={true} className={styles.stickyBottomBorder} />
        </div>
      </div>
      <div
        className={`${styles.navUl} ${showDropdown ? styles.showDropdown : ''}`}
      >
        {Object.entries(anchorTags).map(([tag, data], index) => (
          <>
            <Link
              key={index}
              className={`${styles.responsiveMenu} ${
                activeHash === data.href ? styles.activeItem : ''
              }`}
              {...data}
              scroll={false}
              onClickHandler={() =>
                handleNavClickInternal(data.href, data.text)
              }
            >
              <div className={styles.responsiveContainer}>
                <div className={styles.responsiveTitle}>
                  <Typography variant="h5" color="primary.main">
                    {data.text}
                  </Typography>
                </div>
              </div>
            </Link>
            <Divider
              stretchScreen={true}
              className={styles.stickyBottomBorder}
            />
          </>
        ))}
      </div>
    </div>
  );
};

export default StickyNavBar;
